import { Call, Seat } from "@ravalmatic/bridge-engine/core";
import { NineSliceContainer } from "@ravalmatic/catafractum-utils";
import { BiddingHistoryCell } from "./BiddingHistoryCell";

export class BiddingHistoryContainer extends Phaser.GameObjects.Container {

    private readonly background: NineSliceContainer;
    private readonly glowingBackground: NineSliceContainer;
    private readonly cells: BiddingHistoryCell[];
    private turnActive = false;
    private hiding = false;

    constructor(scene: Phaser.Scene, seat: Seat) {

        super(scene);
        this.x = seat === 1 ? 160 : seat === 3 ? -160 : 0;
        this.y = seat === 0 ? -160 : seat === 2 ? -19 : -50;

        const w = 340;
        const h = seat === 0 || seat === 2 ? 622 : 560;
        const d = 25;

        this.background = new NineSliceContainer(
            scene,
            0,
            0,
            "texture_atlas_1",
            "bidding_history_bg",
            w,
            h,
            d
        );
        this.background.setAngle(seat === 0 ? -90 : seat === 2 ? 90 : 0);
        this.background.setScale(seat === 3 ? -1 : 1, 1);
        this.add(this.background);

        this.glowingBackground = new NineSliceContainer(
            scene,
            0,
            0,
            "texture_atlas_1",
            "bidding_history_glowing_bg",
            w,
            h,
            d
        );
        this.glowingBackground.setAngle(this.background.angle);
        this.glowingBackground.setScale(this.background.scaleX, this.background.scaleY);
        this.glowingBackground.setAlpha(0);
        this.add(this.glowingBackground);

        const isHorizontal = seat === 0 || seat === 2;
        const cellsX = seat === 1 ? -110 : seat === 3 ? 110 : 0;
        const cellsY = seat === 0 ? 112 : seat === 2 ? -112 : 0;
        this.cells = Array.from({ length: 8 }, (_, index) => new BiddingHistoryCell(
            scene,
            isHorizontal ? (index - 3.5) * 72 : cellsX,
            isHorizontal ? cellsY : (index - 3.5) * 64
        ));
        this.add(this.cells);
    }

    public setTurnActive(active: boolean): void {

        if (this.turnActive === active) {
            return;
        }

        this.turnActive = active;
        this.scene.tweens.killTweensOf(this.glowingBackground);
        this.scene.tweens.add({
            targets: this.glowingBackground,
            alpha: active ? 1 : 0,
            duration: 250,
            onComplete: () => {
                if (this.turnActive) {
                    this.scene.tweens.add({
                        targets: this.glowingBackground,
                        alpha: .6,
                        duration: 600,
                        yoyo: true,
                        repeat: -1
                    });
                }
            }
        });
    }

    public destroy(fromScene?: boolean): void {

        this.scene.tweens.killTweensOf(this);
        this.scene.tweens.killTweensOf(this.glowingBackground);
        super.destroy(fromScene);
    }

    public hideAnimated(onComplete?: () => void): void {

        if (!this.visible) {
            onComplete?.();
            return;
        }
        if (this.hiding) {
            throw new Error("Bidding history is already hiding");
        }

        this.hiding = true;
        this.setTurnActive(false);

        this.scene.tweens.add({
            targets: this,
            alpha: 0,
            scaleX: .85,
            scaleY: .85,
            duration: 500,
            ease: "Cubic.In",
            onComplete: () => {
                this.destroy();
                onComplete?.();
            }
        });
    }

    public setCalls(calls: readonly Call[]): void {

        const visibleCalls = calls.slice(-this.cells.length);
        this.cells.forEach((cell, index) => cell.setCall(visibleCalls[index] ?? null));
    }
}
